import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, Spinner } from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import { getScheduledJob } from "services/ScheduleServices";
import JobDetailViewContainer from "modules/analyst/containers/JobDetailView";

const WorkflowDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Full proto stored so WorkflowDetailContainer can use it without a second API call
  const [jobProto, setJobProto] = useState<ScheduledJobProto | undefined>(undefined);
  // Header kept separately so name edits can be reflected in the title row immediately
  const [jobHeader, setJobHeader] = useState<Header | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  // Derive active tab from URL path suffix
  const activeTab = location.pathname.endsWith("/runs") ? 1 : 0;

  useEffect(() => {
    if (!id) {
      navigate("/workflows");
      return;
    }
    const load = async () => {
      setIsLoading(true);
      const job = await getScheduledJob(id, false);
      if (!job || !job.header) {
        navigate("/workflows");
        return;
      }
      setJobProto(job);
      setJobHeader(job.header);
      setIsLoading(false);
      // Default to /definition if landing on bare /workflows/:id
      if (!location.pathname.endsWith("/definition") && !location.pathname.endsWith("/runs")) {
        navigate(`/workflows/${id}/definition`, { replace: true });
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleTabChange = useCallback(
    (idx: number) => {
      if (!id) return;
      navigate(idx === 1 ? `/workflows/${id}/runs` : `/workflows/${id}/definition`, { replace: true });
    },
    [id, navigate]
  );

  const handleJobNameChange = useCallback((newName: string) => {
    setJobHeader(prev => (prev ? { ...prev, name: newName } : prev));
  }, []);

  const handleBack = useCallback(() => navigate("/workflows"), [navigate]);

  if (isLoading) {
    return (
      <Box width="100%" height="100vh" bg="white" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="purple.600" thickness="3px" />
      </Box>
    );
  }

  return (
    <Box width="100%" height="100vh" bg="white" display="flex" flexDirection="column">
      <JobDetailViewContainer
        onBack={handleBack}
        selectedJob={jobHeader}
        initialJobDetails={jobProto}
        onJobNameChange={handleJobNameChange}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
    </Box>
  );
};

export default WorkflowDetailPage;
